<template>
  <div class="q-my-md full-width flex justify-center" ref="messageView" v-if="type === 'bubble'">
    <FlexMessageBubble
      :bubbleMessage="bubbleMessage"
      :bubbleIndex="0"
      :hoverPath="currentHovePath"
      :editPath="editPath"
      :isAdmin="isAdmin"
      @onSelectNode="selectNode"
      @initListNode="initListNode"
    />
  </div>
  <div class="q-my-md full-width flex justify-center" ref="messageView" v-else>
    <div class="LySlider">
      <div class="lyInner">
        <template v-for="(item, index) in carouselMessage.contents" :key="index">
          <FlexMessageBubble
            :bubbleMessage="item"
            :bubbleIndex="index"
            :isChild="true"
            :hoverPath="currentHovePath"
            :editPath="editPath"
            :isAdmin="isAdmin"
            @onSelectNode="selectNode"
            @initListNode="initListNode"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import { maska } from 'maska'
import { Prop } from 'vue-property-decorator'
import { IFlexMessageBubble, IFlexMessageCarousel } from '@/utils/types'
import FlexMessageComponents from '@/components/common/flex-message/FlexMessageComponents.vue'
import FlexMessageBubble from '@/components/common/flex-message/FlexMessageBubble.vue'
import * as htmlToImage from 'html-to-image'
import FlexMessageBubbleTree from '@/components/common/flex-message/FlexMessageBubbleTree.vue'

@Options({
  components: { FlexMessageBubbleTree, FlexMessageBubble, FlexMessageComponents },
  directives: { maska },
  emits: ['onSelectNode', 'initListNode', 'onCaptureMessage', 'update:hoverPath'],
})
export default class FlexMessage extends Vue {
  @Prop()
  type!: string

  @Prop()
  bubbleMessage!: IFlexMessageBubble

  @Prop()
  carouselMessage!: IFlexMessageCarousel

  @Prop()
  hoverPath!: string

  @Prop()
  isChanged!: boolean

  @Prop()
  editPath!: string

  @Prop()
  isAdmin!: boolean

  previewImageUrl = ''
  listNode: string[] = []

  get currentHovePath(): string {
    return this.hoverPath
  }

  set currentHovePath(value: string) {
    this.$emit('update:hoverPath', value)
  }

  selectNode(node, path) {
    this.$emit('onSelectNode', node, path)
  }

  initListNode(path) {
    this.listNode.push(...path)
    this.$emit('initListNode', this.listNode)
  }

  captureMessage() {
    this.currentHovePath = ''

    if (!this.previewImageUrl || this.isChanged) {
      const container = this.$refs.messageView as HTMLElement
      if (!container) return

      let targetNode
      if (this.type === 'bubble') {
        targetNode = container.querySelector('.LyBubble') as HTMLElement
      } else {
        targetNode = container.querySelector('.LySlider') as HTMLElement
      }
      if (!targetNode) return

      const width = targetNode.scrollWidth
      const height = targetNode.scrollHeight

      htmlToImage.toBlob(targetNode, {
        width,
        height,
        pixelRatio: 1,
        style: {
          overflow: 'visible',
          transform: 'scale(1)',
          transformOrigin: 'top left',
          width: `${width}px`,
          height: `${height}px`,
        },
      }).then((blob) => {
        if (!blob) return

        const file = new File([blob], 'message.png', { type: blob.type })

        if (this.previewImageUrl) {
          URL.revokeObjectURL(this.previewImageUrl)
        }
        this.previewImageUrl = URL.createObjectURL(file)

        this.$emit('onCaptureMessage', file, this.previewImageUrl)
      })
    }
  }

  mounted() {
    if (this.type === 'carousel') {
      this.listNode.unshift('carousel')
    }
  }
}
</script>

<style lang="scss">
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  border: 0 solid transparent;
}

.LyGi {
  width: 100%;
  max-width: 500px;
  min-width: 0;
}

.LyMe {
  width: 100%;
  max-width: 300px;
  min-width: 0;
}

.LyKi {
  width: 100%;
  max-width: 260px;
  min-width: 0;
}

.LyHe {
  width: 100%;
  max-width: 241px;
  min-width: 0;
}

.LyDe {
  width: 100%;
  max-width: 220px;
  min-width: 0;
}

.LyMi {
  width: 100%;
  max-width: 160px;
  min-width: 0;
}

.LyNa {
  width: 100%;
  max-width: 120px;
  min-width: 0;
}

.LySlider {
  overflow: hidden;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  max-width: 350px;
}

.LySlider::-webkit-scrollbar {
  display: none
}

.LySlider .lyInner {
  display: -webkit-box;
  display: flex;
  width: 100%
}

.LySlider .lyInner::after {
  -webkit-box-flex: 0;
  flex: none;
  content: "";
  display: block;
  width: 7px;
  height: 1px
}

.LySlider .lyItem {
  -webkit-box-flex: 0;
  flex: none;
  display: inherit;
  width: 80%;
  max-width: 300px;
  margin-left: 7px
}

.LySlider .lyItem + .lyItem {
  margin-left: 9px
}

.LySlider .lyItem.LyGi {
  max-width: 500px
}

.LySlider .lyItem.LyMe {
  max-width: 300px
}

.LySlider .lyItem.LyKi {
  max-width: 260px
}

.LySlider .lyItem.LyHe {
  max-width: 241px
}

.LySlider .lyItem.LyDe {
  max-width: 220px
}

.LySlider .lyItem.LyMi {
  max-width: 160px
}

.LySlider .lyItem.LyNa {
  max-width: 120px
}

.LySlider .T1 {
  -webkit-box-flex: 1;
  flex: 1 0 0
}

@media (-webkit-min-device-pixel-ratio: 1) {
  .LySlider .T1 {
    width:0
  }
}

@media (-webkit-min-device-pixel-ratio: 1) and (min-width: 0.001vmax) {
  .LySlider .T1 {
    width:auto
  }
}

@media (min-resolution: 1dpi) {
  .LySlider .T1 {
    width:auto
  }
}

.LySlider .T1 .t1Header {
  -webkit-box-flex: 0;
  flex: none
}

.LySlider .T1 .t1Hero {
  -webkit-box-flex: 0;
  flex: none
}

.LySlider .T1 .t1Hero:last-child {
  border-radius: 0
}

.LySlider .T1 .t1Body {
  -webkit-box-flex: 1;
  flex: 1 0 0;
  flex-basis: auto
}

@media (-webkit-min-device-pixel-ratio: 1) {
  .LySlider .T1 .t1Body {
    width:0
  }
}

@media (-webkit-min-device-pixel-ratio: 1) and (min-width: 0.001vmax) {
  .LySlider .T1 .t1Body {
    width:auto
  }
}

@media (min-resolution: 1dpi) {
  .LySlider .T1 .t1Body {
    width:auto
  }
}

.LySlider .T1 .t1Body > .MdBx.vr {
  -webkit-box-flex: 1;
  flex: 1 0 0;
  flex-basis: auto
}

.LyGi .T1 {
  border-radius: 5px;
  max-width: 500px;
  margin: 0 7px
}

.LyMe .T1 {
  border-radius: 17px
}

.LyKi .T1 {
  border-radius: 10px
}

.LyHe .T1 {
  border-radius: 10px
}

.LyDe .T1 {
  border-radius: 10px
}

.LyMi .T1 {
  border-radius: 10px
}

.LyNa .T1 {
  border-radius: 10px
}

.T1 {
  border-radius: 17px;
  overflow: hidden;
  background-color: #ffffff;
  display: -webkit-box;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  flex-direction: column
}

.T1 .t1Body,.T1 .t1Footer,.T1 .t1Header,.T1 .t1Hero {
  display: inherit;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  flex-direction: column
}

.T1 .t1Body > div,.T1 .t1Footer > div,.T1 .t1Header > div,.T1 .t1Hero > div {
  flex-basis: auto
}

.T1 .t1Hero:first-child,.T1 .t1Hero:first-child .MdImg.ExFull,.T1 .t1Hero:first-child .MdImg.ExFull > div,.T1 .t1Hero:first-child .MdImg.ExFull > div > a,.T1 .t1Hero:first-child .MdImg.ExFull > div > a > span {
  border-top-left-radius: inherit;
  border-top-right-radius: inherit
}

.T1 .t1Hero:last-child,.T1 .t1Hero:last-child .MdImg.ExFull,.T1 .t1Hero:last-child .MdImg.ExFull > div,.T1 .t1Hero:last-child .MdImg.ExFull > div > a,.T1 .t1Hero:last-child .MdImg.ExFull > div > a > span {
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit
}

.T1 .t1Hero:first-child .MdImg.ExFull > div > a > span,.T1 .t1Hero:last-child .MdImg.ExFull > div > a > span {
  overflow: hidden
}

.T1 .t1Header > .MdBx {
  padding: 20px
}

.LyKi .T1 .t1Header > .MdBx {
  padding: 13px
}

.LyHe .T1 .t1Header > .MdBx {
  padding: 11px 14px 13px
}

.LyDe .T1 .t1Header > .MdBx {
  padding: 11px 14px 13px
}

.LyMi .T1 .t1Header > .MdBx {
  padding: 10px
}

.LyNa .T1 .t1Header > .MdBx {
  padding: 10px
}

.T1 .t1Body > .MdBx {
  padding: 20px;
  padding-top: 19px
}

.LyKi .T1 .t1Body > .MdBx {
  padding: 13px
}

.LyHe .T1 .t1Body > .MdBx {
  padding: 11px 14px 13px
}

.LyDe .T1 .t1Body > .MdBx {
  padding: 11px 14px 13px
}

.LyMi .T1 .t1Body > .MdBx {
  padding: 10px
}

.LyNa .T1 .t1Body > .MdBx {
  padding: 10px
}

.T1 .t1Body.ExHasFooter > .MdBx {
  padding-bottom: 10px
}

.LyKi .T1 .t1Body.ExHasFooter > .MdBx {
  padding-bottom: 17px
}

.LyHe .T1 .t1Body.ExHasFooter > .MdBx {
  padding-bottom: 17px
}

.LyDe .T1 .t1Body.ExHasFooter > .MdBx {
  padding-bottom: 17px
}

.T1 .t1Footer > .MdBx {
  padding: 10px
}

.fxLTR,[dir="ltr"] {
  direction: ltr
}

.fxRTL,[dir="rtl"] {
  direction: rtl
}

.MdBtn,.MdBx,.MdIco,.MdImg,.MdTxt,[class*="MdSep"] {
  display: -webkit-box;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  flex-direction: column;
  -webkit-box-flex: 1;
  flex: 1 0 0;
  min-width: 0;
  max-width: 100%
}

@media (-webkit-min-device-pixel-ratio: 1) {
  .MdBtn,.MdBx,.MdIco,.MdImg,.MdTxt,[class*="MdSep"] {
    width:0
  }
}

@media (-webkit-min-device-pixel-ratio: 1) and (min-width: 0.001vmax) {
  .MdBtn,.MdBx,.MdIco,.MdImg,.MdTxt,[class*="MdSep"] {
    width:auto
  }
}

@media (min-resolution: 1dpi) {
  .MdBtn,.MdBx,.MdIco,.MdImg,.MdTxt,[class*="MdSep"] {
    width:auto
  }
}

.MdBx {
  overflow: hidden;
  position: relative
}

.MdBx.vr > div {
  -webkit-box-flex: 0;
  flex: none;
  flex-basis: auto;
}

.MdBx.hr {
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  flex-direction: row;
  width: 100%
}

.MdBx.hr > div {
  overflow: hidden;
  -webkit-box-flex: 1;
  flex: 1 0 0
}

@media (-webkit-min-device-pixel-ratio: 1) {
  .MdBx.hr > div {
    /* width:0; */
  }
}

@media (-webkit-min-device-pixel-ratio: 1) and (min-width: 0.001vmax) {
  .MdBx.hr > div {
    /* width:auto; */
  }
}

@media (min-resolution: 1dpi) {
  .MdBx.hr > div {
    width:auto;
  }
}

.MdBx.hr > div.MdSep {
  overflow: visible
}

.MdBx.hr > div.fl0 {
  width: auto;
}

.MdBx.hr > div.fl0.MdImg.ExFull {
  width: 100%
}

.MdBx.hr > div[class*="MdSep"] {
  -webkit-box-flex: 0;
  flex: none
}

.MdBx.hr.bl {
  -webkit-box-align: baseline;
  align-items: baseline
}

.MdBx.hr > div.ExAbs {
  width: auto
}

.MdBx.hr.ExAbs {
  width: auto
}

.MdBx.hr.ExAbs > div {
  width: auto
}

.ExAbs .MdBx.hr {
  width: auto
}

.ExAbs .MdBx.hr > div {
  width: auto
}

.T1 .MdBx.hr.spcXs > div {
  margin-left: 2px
}

.T1 .MdBx.hr.spcSm > div {
  margin-left: 4px
}

.T1 .MdBx.hr.spcMd > div {
  margin-left: 8px
}

.T1 .MdBx.hr.spcLg > div {
  margin-left: 12px
}

.T1 .MdBx.hr.spcXl > div {
  margin-left: 16px
}

.T1 .MdBx.hr.spcXXl > div {
  margin-left: 20px
}

.T1 .MdBx.hr > div.mdBxFiller,.T1 .MdBx.hr > div.mdBxSpacer,.T1 .MdBx.hr > div:first-child {
  margin-left: 0
}

.T1.fxRTL .MdBx.hr > div {
  margin-left: 0
}

.T1.fxRTL .MdBx.hr.spcXs > div {
  margin-right: 2px
}

.T1.fxRTL .MdBx.hr.spcSm > div {
  margin-right: 4px
}

.T1.fxRTL .MdBx.hr.spcMd > div {
  margin-right: 8px
}

.T1.fxRTL .MdBx.hr.spcLg > div {
  margin-right: 12px
}

.T1.fxRTL .MdBx.hr.spcXl > div {
  margin-right: 16px
}

.T1.fxRTL .MdBx.hr.spcXXl > div {
  margin-right: 20px
}

.T1.fxRTL .MdBx.hr > div.mdBxFiller,.T1.fxRTL .MdBx.hr > div.mdBxSpacer,.T1.fxRTL .MdBx.hr > div:first-child {
  margin-right: 0
}

.MdBx.itms-jfcS {
  -webkit-box-pack: start;
  justify-content: flex-start
}

.MdBx.itms-jfcE {
  -webkit-box-pack: end;
  justify-content: flex-end
}

.MdBx.itms-jfcC {
  -webkit-box-pack: center;
  justify-content: center
}

.MdBx.itms-jfcSB {
  -webkit-box-pack: justify;
  justify-content: space-between
}

.MdBx.itms-jfcSA {
  justify-content: space-around
}

.MdBx.itms-jfcSE {
  justify-content: space-evenly
}

.MdBx.itms-algS {
  -webkit-box-align: start;
  align-items: flex-start
}

.MdBx.itms-algE {
  -webkit-box-align: end;
  align-items: flex-end
}

.MdBx.itms-algC {
  -webkit-box-align: center;
  align-items: center
}

.MdBx.itms-algBL {
  -webkit-box-align: baseline;
  align-items: baseline
}

.MdBx.itms-algSR {
  -webkit-box-align: stretch;
  align-items: stretch
}

.MdBx.vr.spcXs > div {
  margin-top: 2px
}

.MdBx.vr.spcSm > div {
  margin-top: 4px
}

.MdBx.vr.spcMd > div {
  margin-top: 8px
}

.MdBx.vr.spcLg > div {
  margin-top: 12px
}

.MdBx.vr.spcXl > div {
  margin-top: 16px
}

.MdBx.vr.spcXXl > div {
  margin-top: 20px
}

.MdBx.vr > div.mdBxFiller,.MdBx.vr > div.mdBxSpacer,.MdBx.vr > div:first-child {
  margin-top: 0;
}

.MdBx > div.fl0 {
  -webkit-box-flex: 0!important;
  flex: none!important
}

.MdBx > div.fl1 {
  -webkit-box-flex: 1!important;
  flex: 1 0 0!important
}

.MdBx > div.fl2 {
  -webkit-box-flex: 2!important;
  flex: 2 0 0!important
}

.MdBx > div.fl3 {
  -webkit-box-flex: 3!important;
  flex: 3 0 0!important
}

.MdBx > div.algS {
  -webkit-box-align: start;
  align-items: flex-start
}

.MdBx > div.algE {
  -webkit-box-align: end;
  align-items: flex-end
}

.MdBx > div.grvC {
  -webkit-box-pack: center;
  justify-content: center
}

.MdBx > div.grvB {
  -webkit-box-pack: end;
  justify-content: flex-end
}

.T1 .MdBx > div.mdBxFiller {
  -webkit-box-flex: 1;
  flex: 1 0 0
}

@media (-webkit-min-device-pixel-ratio: 1) {
  .T1 .MdBx > div.mdBxFiller {
    width:0
  }
}

@media (-webkit-min-device-pixel-ratio: 1) and (min-width: 0.001vmax) {
  .T1 .MdBx > div.mdBxFiller {
    width:auto
  }
}

@media (min-resolution: 1dpi) {
  .T1 .MdBx > div.mdBxFiller {
    width:auto
  }
}

.MdBx > div.mdBxSpacer {
  -webkit-box-flex: 0;
  flex: none
}

.MdBx.hr > div.mdBxSpacer {
  height: 1px
}

.MdBx.hr > div.mdBxSpacer.spcXs {
  width: 2px
}

.MdBx.hr > div.mdBxSpacer.spcSm {
  width: 4px
}

.MdBx.hr > div.mdBxSpacer.spcMd {
  width: 8px
}

.MdBx.hr > div.mdBxSpacer.spcLg {
  width: 12px
}

.MdBx.hr > div.mdBxSpacer.spcXl {
  width: 16px
}

.MdBx.hr > div.mdBxSpacer.spcXXl {
  width: 20px
}

.MdBx.hr[class*="spc"] > div.mdBxSpacer + div {
  margin-left: 0
}

.MdBx.hr > .MdSep {
  align-self: stretch
}

.MdBx.vr > div.mdBxSpacer {
  width: 1px
}

.MdBx.vr > div.mdBxSpacer.spcXs {
  height: 2px
}

.MdBx.vr > div.mdBxSpacer.spcSm {
  height: 4px
}

.MdBx.vr > div.mdBxSpacer.spcMd {
  height: 8px
}

.MdBx.vr > div.mdBxSpacer.spcLg {
  height: 12px
}

.MdBx.vr > div.mdBxSpacer.spcXl {
  height: 16px
}

.MdBx.vr > div.mdBxSpacer.spcXXl {
  height: 20px
}

.MdBx.vr > div.fl0 {
  -webkit-box-flex: 0!important;
  flex: none!important
}

.MdBx.vr > div.fl1 {
  -webkit-box-flex: 1!important;
  flex: 1 0 auto!important
}

.MdBx.vr > div.fl2 {
  -webkit-box-flex: 2!important;
  flex: 2 0 auto!important
}

.MdBx.vr > div.fl3 {
  -webkit-box-flex: 3!important;
  flex: 3 0 auto!important
}

.MdBx[class*="spc"] > div.mdBxSpacer + div {
  margin-top: 0
}

[class*="MdSep"] {
  -webkit-box-flex: 0;
  flex: none;
  box-sizing: content-box;
  width: 100%;
  border: 0 solid #d4d6da
}

.MdSep {
  position: relative;
  -webkit-box-flex: 0;
  flex: none
}

.MdSep::before {
  box-sizing: content-box;
  content: "";
  display: block;
  position: absolute;
  z-index: 1;
  width: 100%;
  height: 1px;
  border: inherit;
  border-top-width: 1px;
  -webkit-transform-origin: left top;
  transform-origin: left top;
  -webkit-transform: scaleY(0.5);
  transform: scaleY(0.5)
}

.MdSep:last-child::before {
  margin-top: -1px;
  border-top-width: 0;
  border-bottom-width: 1px
}

.fxRTL .MdSep::before {
  -webkit-transform-origin: right top;
  transform-origin: right top
}

.MdSepB {
  border-top-width: 1px
}

.MdBx.hr > div.MdSep::before {
  width: 1px;
  height: 100%;
  border-top-width: 0;
  border-left-width: 1px;
  -webkit-transform: scaleX(0.5);
  transform: scaleX(0.5)
}

.MdBx.hr > div.MdSep:last-child::before {
  margin-top: 0;
  margin-left: -1px;
  border-left-width: 0;
  border-right-width: 1px
}

.MdBx.hr > div.MdSepB {
  border-top-width: 0;
  border-left-width: 1px
}

.fxRTL .MdBx.hr > div.MdSep::before {
  border-left-width: 0;
  border-right-width: 1px
}

.fxRTL .MdBx.hr > div.MdSep:last-child::before {
  margin-left: 0;
  margin-right: -1px;
  border-right-width: 0;
  border-left-width: 1px
}

.MdTxt {
  position: relative
}

.MdTxt p {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap
}

.MdTxt p:empty {
  height: 1.4em
}

.MdTxt.ExWrap p {
  text-overflow: clip;
  word-wrap: break-word;
  white-space: normal
}

.MdTxt.Ex2nd {
  color: #aaa
}

.MdTxt.ExAlgC p {
  text-align: center
}

.MdTxt.ExAlgE p {
  text-align: end
}

.MdSpn {
  font-style: inherit;
  font-weight: inherit;
  text-decoration: inherit
}

.MdSpn.ExXXs {
  font-size: 11px
}

.MdSpn.ExXs {
  font-size: 13px
}

.MdSpn.ExSm {
  font-size: 14px
}

.MdSpn.ExMd {
  font-size: 16px
}

.MdSpn.ExLg {
  font-size: 19px
}

.MdSpn.ExXl {
  font-size: 22px
}

.MdSpn.ExXXl {
  font-size: 29px
}

.MdSpn.Ex3Xl {
  font-size: 35px
}

.MdSpn.Ex4Xl {
  font-size: 48px
}

.MdSpn.Ex5Xl {
  font-size: 74px
}

.MdIco {
  position: relative
}

.MdIco span {
  display: inline-block;
  overflow: hidden;
  background: no-repeat center;
  background-size: contain;
  width: 1em;
  height: 1em;
  vertical-align: baseline
}

.MdIco.ExAr2to1 span {
  width: 2em
}

.MdIco.ExAr3to1 span {
  width: 3em
}

.MdIco.ExXXs,.MdTxt.ExXXs {
  font-size: 11px
}

.MdIco.ExXs,.MdTxt.ExXs {
  font-size: 13px
}

.MdIco.ExSm,.MdTxt.ExSm {
  font-size: 14px
}

.MdIco.ExMd,.MdTxt.ExMd {
  font-size: 16px
}

.MdIco.ExLg,.MdTxt.ExLg {
  font-size: 19px
}

.MdIco.ExXl,.MdTxt.ExXl {
  font-size: 22px
}

.MdIco.ExXXl,.MdTxt.ExXXl {
  font-size: 29px
}

.MdIco.Ex3Xl,.MdTxt.Ex3Xl {
  font-size: 35px
}

.MdIco.Ex4Xl,.MdTxt.Ex4Xl {
  font-size: 48px
}

.MdIco.Ex5Xl,.MdTxt.Ex5Xl {
  font-size: 74px
}

.MdBtn {
  position: relative
}

.MdBtn a {
  display: inherit;
  -webkit-box-align: center;
  align-items: center;
  -webkit-box-pack: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 16px
}

.MdBtn div {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%
}

.MdBtn.ExBtn1 a {
  background-color: #17c950;
  color: #ffffff
}

.MdBtn.ExBtn2 a {
  background-color: #dcdfe5;
  color: #111111
}

.MdBtn.ExBtnL a {
  color: #42659a
}

.MdBtn.ExSm a {
  height: 40px
}

.MdImg {
  -webkit-box-align: center;
  align-items: center;
  overflow: hidden;
  position: relative
}

.MdImg > div {
  max-width: 100%
}

.MdImg a {
  display: block;
  position: relative;
  width: 100%
}

.MdImg span {
  display: block;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  overflow: hidden;
  background-repeat: no-repeat;
  background-position: center center
}

.MdImg.ExXXs > div {
  width: 40px
}

.MdImg.ExXs > div {
  width: 60px
}

.MdImg.ExSm > div {
  width: 80px
}

.MdImg.ExMd > div {
  width: 100px
}

.MdImg.ExLg > div {
  width: 120px
}

.MdImg.ExXl > div {
  width: 140px
}

.MdImg.ExXXl > div {
  width: 160px
}

.MdImg.Ex3Xl > div {
  width: 180px
}

.MdImg.Ex4Xl > div {
  width: 200px
}

.MdImg.Ex5Xl > div {
  width: 220px
}

.MdImg.ExFull > div {
  width: 100%
}

.MdImg.ExAr1to1 a {
  padding-bottom: 100%
}

.MdImg.ExAr1_51to1 a {
  padding-bottom: 66.22517%
}

.MdImg.ExAr1_91to1 a {
  padding-bottom: 52.35602%
}

.MdImg.ExAr20to13 a {
  padding-bottom: 65%
}

.MdImg.ExAr4to3 a {
  padding-bottom: 75%
}

.MdImg.ExAr16to9 a {
  padding-bottom: 56.25%
}

.MdImg.ExAr2to1 a {
  padding-bottom: 50%
}

.MdImg.ExAr3to1 a {
  padding-bottom: 33.33333%
}

.MdImg.ExAr3to4 a {
  padding-bottom: 133.33333%
}

.MdImg.ExAr9to16 a {
  padding-bottom: 177.77778%
}

.MdImg.ExAr1to2 a {
  padding-bottom: 200%
}

.MdImg.ExAr1to3 a {
  padding-bottom: 300%
}

.MdImg.ExFit span {
  background-size: contain
}

.MdImg.ExCover span {
  background-size: cover
}

.MdImg.ExCircle span {
  border-radius: 50%
}

.MdImg.ExMirrored span {
  -webkit-transform: scaleX(-1);
  transform: scaleX(-1)
}

.MdImg.ExAbs.ExFull {
  width: 100%
}

.ExMgnANone {
  margin: 0!important
}

.ExMgnAXs {
  margin: 2px!important
}

.ExMgnASm {
  margin: 4px!important
}

.ExMgnAMd {
  margin: 8px!important
}

.ExMgnALg {
  margin: 12px!important
}

.ExMgnAXl {
  margin: 16px!important
}

.ExMgnAXXl {
  margin: 20px!important
}

.ExMgnTNone {
  margin-top: 0!important
}

.ExMgnTXs {
  margin-top: 2px!important
}

.ExMgnTSm {
  margin-top: 4px!important
}

.ExMgnTMd {
  margin-top: 8px!important;
}

.ExMgnTLg {
  margin-top: 12px!important
}

.ExMgnTXl {
  margin-top: 16px!important
}

.ExMgnTXXl {
  margin-top: 20px!important
}

.ExMgnBNone {
  margin-bottom: 0!important
}

.ExMgnBXs {
  margin-bottom: 2px!important
}

.ExMgnBSm {
  margin-bottom: 4px!important
}

.ExMgnBMd {
  margin-bottom: 8px!important
}

.ExMgnBLg {
  margin-bottom: 12px!important
}

.ExMgnBXl {
  margin-bottom: 16px!important
}

.ExMgnBXXl {
  margin-bottom: 20px!important
}

.ExMgnLNone {
  margin-left: 0!important
}

.ExMgnLXs {
  margin-left: 2px!important
}

.ExMgnLSm {
  margin-left: 4px!important
}

.ExMgnLMd {
  margin-left: 8px!important
}

.ExMgnLLg {
  margin-left: 12px!important
}

.ExMgnLXl {
  margin-left: 16px!important
}

.ExMgnLXXl {
  margin-left: 20px!important
}

.ExMgnRNone {
  margin-right: 0!important
}

.ExMgnRXs {
  margin-right: 2px!important
}

.ExMgnRSm {
  margin-right: 4px!important
}

.ExMgnRMd {
  margin-right: 8px!important
}

.ExMgnRLg {
  margin-right: 12px!important
}

.ExMgnRXl {
  margin-right: 16px!important
}

.ExMgnRXXl {
  margin-right: 20px!important
}

.ExPadANone {
  padding: 0!important
}

.ExPadAXs {
  padding: 2px!important
}

.ExPadASm {
  padding: 4px!important
}

.ExPadAMd {
  padding: 8px!important
}

.ExPadALg {
  padding: 12px!important
}

.ExPadAXl {
  padding: 16px!important
}

.ExPadAXXl {
  padding: 20px!important
}

.ExPadTNone {
  padding-top: 0!important
}

.ExPadTXs {
  padding-top: 2px!important
}

.ExPadTSm {
  padding-top: 4px!important
}

.ExPadTMd {
  padding-top: 8px!important
}

.ExPadTLg {
  padding-top: 12px!important
}

.ExPadTXl {
  padding-top: 16px!important
}

.ExPadTXXl {
  padding-top: 20px!important
}

.ExPadBNone {
  padding-bottom: 0!important
}

.ExPadBXs {
  padding-bottom: 2px!important
}

.ExPadBSm {
  padding-bottom: 4px!important
}

.ExPadBMd {
  padding-bottom: 8px!important
}

.ExPadBLg {
  padding-bottom: 12px!important
}

.ExPadBXl {
  padding-bottom: 16px!important
}

.ExPadBXXl {
  padding-bottom: 20px!important
}

.ExPadLNone {
  padding-left: 0!important
}

.ExPadLXs {
  padding-left: 2px!important
}

.ExPadLSm {
  padding-left: 4px!important
}

.ExPadLMd {
  padding-left: 8px!important
}

.ExPadLLg {
  padding-left: 12px!important
}

.ExPadLXl {
  padding-left: 16px!important
}

.ExPadLXXl {
  padding-left: 20px!important
}

.ExPadRNone {
  padding-right: 0!important
}

.ExPadRXs {
  padding-right: 2px!important
}

.ExPadRSm {
  padding-right: 4px!important
}

.ExPadRMd {
  padding-right: 8px!important
}

.ExPadRLg {
  padding-right: 12px!important
}

.ExPadRXl {
  padding-right: 16px!important
}

.ExPadRXXl {
  padding-right: 20px!important
}

.ExBdrWdtNone {
  border-style: solid;
  border-width: 0!important
}

.ExBdrWdtLgh {
  border-style: solid;
  border-width: 0.5px!important
}

.ExBdrWdtNml {
  border-style: solid;
  border-width: 1px!important
}

.ExBdrWdtMdm {
  border-style: solid;
  border-width: 2px!important
}

.ExBdrWdtSbd {
  border-style: solid;
  border-width: 3px!important
}

.ExBdrWdtBld {
  border-style: solid;
  border-width: 4px!important
}

.ExBdrRadNone {
  border-radius: 0!important
}

.ExBdrRadXs {
  border-radius: 2px!important
}

.ExBdrRadSm {
  border-radius: 4px!important
}

.ExBdrRadMd {
  border-radius: 8px!important
}

.ExBdrRadLg {
  border-radius: 12px!important
}

.ExBdrRadXl {
  border-radius: 16px!important
}

.ExBdrRadXXl {
  border-radius: 20px!important
}

.ExAbs {
  position: absolute;
  margin: 0!important;
  width: auto
}

.ExTNone {
  top: 0
}

.ExTXs {
  top: 2px
}

.ExTSm {
  top: 4px
}

.ExTMd {
  top: 8px
}

.ExTLg {
  top: 12px
}

.ExTXl {
  top: 16px
}

.ExTXXl {
  top: 20px
}

.ExBNone {
  bottom: 0
}

.ExBXs {
  bottom: 2px
}

.ExBSm {
  bottom: 4px
}

.ExBMd {
  bottom: 8px
}

.ExBLg {
  bottom: 12px
}

.ExBXl {
  bottom: 16px
}

.ExBXXl {
  bottom: 20px
}

.ExLNone {
  left: 0
}

.ExLXs {
  left: 2px
}

.ExLSm {
  left: 4px
}

.ExLMd {
  left: 8px
}

.ExLLg {
  left: 12px
}

.ExLXl {
  left: 16px
}

.ExLXXl {
  left: 20px
}

.ExRNone {
  right: 0
}

.ExRXs {
  right: 2px
}

.ExRSm {
  right: 4px
}

.ExRMd {
  right: 8px
}

.ExRLg {
  right: 12px
}

.ExRXl {
  right: 16px
}

.ExRXXl {
  right: 20px
}

.ExWR {
  font-weight: normal!important
}

.ExWR * {
  font-weight: inherit
}

.ExWB {
  font-weight: bold!important
}

.ExWB * {
  font-weight: inherit
}

.ExFntStyNml {
  font-style: normal!important
}

.ExFntStyNml * {
  font-style: inherit
}

.ExFntStyIt {
  font-style: italic!important
}

.ExFntStyIt * {
  font-style: inherit
}

.ExTxtDecNone {
  text-decoration: none!important
}

.ExTxtDecUl {
  text-decoration: underline!important
}

.ExTxtDecLt {
  text-decoration: line-through!important
}
@media screen and (min-width: 1920px) {
  .LySlider {
    max-width: 500px;
  }
}
</style>
